import { areAllAdvancementRequirementsApproved } from '@bsa/scouting-js-utils/src/requirements';
import { createSelector } from '@reduxjs/toolkit';
import { get, isEmpty, isNil, orderBy } from 'lodash';
import { DateTime } from 'luxon';
import moment from 'moment';
import stable from 'stable';

import type {
  AdvancementVersion,
  AdvancementVersionNormalized,
  RankVersion,
} from '@appTypes/common/versions';
import { meritBadgesSel } from '@dict/duck/selectorsTyped';
import { routeParamsSel } from '@location/duck/selectorsTyped';
import { NOA_AWARDS, PALM_AWARDS } from '@modules/advancement/constants';
import type { Rank } from '@modules/bulkEntry/duck/types';
import { typeSel } from '@modules/location/duck/selectors';
import { isProfileRouteSel } from '@modules/profile/duck/selectors';
import { hasEditOptOutPerms } from '@modules/user/permissionChecks';
import { youthsAndAdultsSel } from '@progress/activities';
import { advancementTypes } from '@shared/constants';
import { ROUTE_PROFILE } from '@shared/duck/actions';
import {
  hasNewPermissionSel,
  isParentGuardianSel,
  isYouthMemberSel,
  organizationGuidSel,
  organizationPositionSel,
  ranksSel as rankDictSel,
  unitTypeIdSel,
  urlUserIdSel,
} from '@shared/duck/selectors/general.selectors';
import { userIdSel as selfUserIdSel } from '@user/duck/selectors';
import { currentUserAgeSel } from '@user/duck/selectorsTyped';

import type { RootState } from '../../../root/store';
import {
  advancementsStates,
  moduleName,
  youthEditApprovePermissions,
} from '../constants';
import type {
  RouteAdvancementRequirements,
  RouteAdvancementsList,
  RouteYouthActivityLogs,
} from '../routes.types';
import { getAdvancementUid } from '../utils';
import reducers from './reducersTyped';
import {
  highestRankSel,
  memberDetailsSel,
  youthAdvRequirementsSel,
  youthProfileInfoSel,
} from './selectors';

type RankStates = (typeof advancementsStates)[keyof typeof advancementsStates];
type RankWithState = Rank & { state: RankStates };

export const moduleSel = (state: RootState): ReturnType<typeof reducers> =>
  state[moduleName];

const youthAdvRequirementsParamsSel = (state: RootState) =>
  routeParamsSel<RouteAdvancementRequirements>(state);

export const advRequirementsMeritBadgeNameSel = createSelector(
  youthAdvRequirementsParamsSel,
  meritBadgesSel,
  youthAdvRequirementsSel,
  (
    { advancementType, advancementId },
    meritBadges,
    advRequirements,
  ): string | undefined => {
    if (advancementType !== advancementTypes.MERIT_BADGES) return undefined;

    const meritBadge = meritBadges.find(
      mb => Number(mb.id) === Number(advancementId),
    );
    return meritBadge?.name || advRequirements?.name;
  },
);

const advancementsVersionsSel = (state: RootState) =>
  moduleSel(state).advancementsVersions;

/**
 * Used to get payload when navigating to route for advancement index
 * @see {RouteAdvancementsList}
 */
export const advancementListPagePayloadSel = createSelector(
  routeParamsSel<RouteAdvancementsList>,
  state => {
    const payload = state || {};
    return {
      payload,
      isMeritBadges: state.advancementType === advancementTypes.MERIT_BADGES,
    };
  },
);

export const advReqEditLoadingSel = createSelector(
  moduleSel,
  ({ advReqEditLoading }) => advReqEditLoading,
);

export const advReqEditStopLoadingSel = createSelector(
  moduleSel,
  ({ advReqEditStopLoading }) => advReqEditStopLoading,
);

export const multipleBlueCardModalOpenSel = createSelector(
  moduleSel,
  ({ multipleBlueCardModalOpen }) => multipleBlueCardModalOpen,
);

const getRankState = (rank: Rank, highestRank: Rank) => {
  if (!highestRank) return advancementsStates.NEXT;
  const highestRankLevel = Number(highestRank.level);
  const rankLevel = Number(rank.level);

  if (highestRankLevel > rankLevel) {
    return advancementsStates.PREVIOUS;
  } else if (highestRankLevel === rankLevel) {
    return advancementsStates.LAST_APPROVED;
  } else {
    return advancementsStates.NEXT;
  }
};

export const getRanks = ({
  ranksList,
  recordedRanks,
  highestRank,
}: {
  ranksList: Rank[];
  recordedRanks: Rank[];
  highestRank: Rank;
}): RankWithState[] =>
  ranksList
    .map((rank: Rank) => {
      const recordedRank = recordedRanks.find(({ id }) => id == rank.id);
      if (recordedRank) {
        return recordedRank;
      }
      return { ...rank, uid: getAdvancementUid(rank, advancementTypes.RANKS) };
    })
    .map((rank: Rank) => ({
      ...rank,
      state: getRankState(rank, highestRank),
    }));

export const unitRanksSel = createSelector(
  rankDictSel,
  unitTypeIdSel,
  (_state: RootState, unitTypeId: number) => unitTypeId,
  (ranks, unitTypeId, customUnitTypeId) => {
    const selectedProgramId = customUnitTypeId || unitTypeId;
    return ranks.filter(
      ({ programId }: { programId: string | number }) =>
        selectedProgramId == programId,
    );
  },
);

export const ranksSel = createSelector(
  [
    highestRankSel,
    memberDetailsSel,
    (state: RootState, unitTypeId: number) => unitRanksSel(state, unitTypeId),
  ],
  (highestRank, memberDetails, ranksList) => {
    const recordedRanks = memberDetails.ranks || [];
    const ranks = getRanks({ ranksList, recordedRanks, highestRank });
    const sortedRanks = stable(
      ranks,
      (a, b) => Number(a.level) > Number(b.level),
    );
    return sortedRanks;
  },
);

/** This is used in the user profile page */
export const canEditOptOutSel = createSelector(
  isParentGuardianSel,
  organizationPositionSel,
  typeSel,
  currentUserAgeSel,
  (isParentGuardian, position, pageType, currentUserAge) => {
    const isSelfProfile = pageType === ROUTE_PROFILE;
    const isInvalidYouth = isSelfProfile && currentUserAge < 18;
    if (isInvalidYouth) return false;

    const hasPermissions = hasEditOptOutPerms(position);
    const valid = hasPermissions || isParentGuardian || isSelfProfile;
    return valid;
  },
);

export const advancementsVersionsPerAdvTypeSel = createSelector(
  advancementsVersionsSel,
  youthAdvRequirementsParamsSel,
  (versions, params) => {
    const { advancementType, advancementId } = params;

    // Order by versionEffectiveDt descending
    const advVersions = get(
      versions,
      `${advancementType}-${advancementId}`,
      [],
    ) as AdvancementVersionNormalized[];
    return [...advVersions].sort((a, b) =>
      moment(b.versionEffectiveDt).diff(moment(a.versionEffectiveDt)),
    );
  },
);

export const nonExpiredAdvVersionsSel = createSelector(
  youthAdvRequirementsSel,
  advancementsVersionsPerAdvTypeSel,
  (youthReqs, versions) => {
    const { versionId: activeVersionId } = youthReqs;
    const now = DateTime.now();

    // versions are already sorted by versionEffectiveDt descending
    const nonExpiredVersions = versions.filter(version => {
      const advancementVersion = version as AdvancementVersionNormalized;
      const lacksExpiredDate =
        isNil(advancementVersion.expiredDate) ||
        isEmpty(advancementVersion.expiredDate);
      //expiredDate is the "scoutbook" expired date which controls whether we should hide the version
      const expiredDate = DateTime.fromISO(
        (advancementVersion.expiredDate as string) ?? '',
      );

      return lacksExpiredDate || expiredDate > now;
    });

    // also make an exception for rank versions that are already Active.
    const activeVersion = versions.find(
      version => version.versionId === activeVersionId,
    );

    return {
      activeVersion,
      nonExpiredVersions,
    };
  },
);

export const validAdvVersionsSel = createSelector(
  nonExpiredAdvVersionsSel,
  nonExpiredAdvVersions => {
    const { activeVersion, nonExpiredVersions } = nonExpiredAdvVersions;

    const latestTwoVersions = nonExpiredVersions.slice(0, 2);

    const isActiveNotInTwoLatest =
      activeVersion &&
      !latestTwoVersions.some(
        version => version.versionId === activeVersion.versionId,
      );

    return isActiveNotInTwoLatest
      ? [...latestTwoVersions, activeVersion]
      : latestTwoVersions;
  },
);

export const meritBadgeValidVersionsSel = createSelector(
  nonExpiredAdvVersionsSel,
  nonExpiredAdvVersions => {
    const { activeVersion, nonExpiredVersions } = nonExpiredAdvVersions;
    const now = DateTime.now();
    const tenYearsAgo = now.minus({ years: 10 });
    const lastTenYearsVersions = nonExpiredVersions.filter(ver => {
      const expiry = DateTime.fromISO(ver.versionExpiryDt as string);
      // Not expired version
      if (!expiry.isValid) return true;

      // Keep only if expiry is within last 10 years
      return expiry >= tenYearsAgo && expiry <= now;
    });

    const isActiveNotInLastTen =
      activeVersion &&
      !lastTenYearsVersions.some(
        version => version.versionId === activeVersion.versionId,
      );

    return isActiveNotInLastTen
      ? [...lastTenYearsVersions, activeVersion]
      : lastTenYearsVersions;
  },
);

export const advancementsValidVersionsSel = createSelector(
  validAdvVersionsSel,
  meritBadgeValidVersionsSel,
  youthAdvRequirementsParamsSel,
  (versions, mbVersions, params) => {
    const { advancementType } = params;
    return advancementType === advancementTypes.MERIT_BADGES
      ? mbVersions
      : versions;
  },
);

export const versionExpiryDateSel = createSelector(
  youthAdvRequirementsSel,
  advancementsVersionsPerAdvTypeSel,
  (requirements, versions) => {
    const { versionId: activeVersionId } = requirements;
    const activeVersion = versions.find(
      version => version.versionId === activeVersionId,
    );
    const expiryDate = activeVersion ? activeVersion.versionExpiryDt : null;
    return expiryDate;
  },
);
export const versionEffectiveDateSel = createSelector(
  youthAdvRequirementsSel,
  advancementsVersionsPerAdvTypeSel,
  (requirements, versions) => {
    const { versionId: activeVersionId } = requirements;
    const activeVersion = versions.find(
      version => version.versionId === activeVersionId,
    );
    const effectiveDate = activeVersion
      ? activeVersion.versionEffectiveDt
      : null;
    return effectiveDate;
  },
);
export const newestVersionSel = createSelector(
  advancementsVersionsPerAdvTypeSel,
  versions => versions[0] || ({} as AdvancementVersion),
);
export const effectiveVersionSel = createSelector(
  advancementsVersionsPerAdvTypeSel,
  versions =>
    versions.find(version => {
      const versionStart = moment(version.versionEffectiveDt);
      const versionEnd = moment(version.versionExpiryDt);

      return (
        versionStart.isBefore(moment()) &&
        (versionEnd.isAfter(moment()) || !version.versionExpiryDt)
      );
    }) || ({} as AdvancementVersionNormalized),
);

export const sortedExpiredAdvVersionsSel = createSelector(
  advancementsVersionsPerAdvTypeSel,
  versions => {
    const now = moment();
    const expiredVersions = versions.filter(version => {
      const rankVersion = version as RankVersion;
      const hasExpiredDate = !isEmpty(rankVersion.expiredDate);
      const expiredDate = moment(rankVersion.expiredDate);

      return hasExpiredDate || expiredDate.isBefore(now);
    });

    return orderBy(expiredVersions, ['versionId'], ['desc']);
  },
);

export const isVersionArchivedSel = createSelector(
  sortedExpiredAdvVersionsSel,
  (_: RootState, versionId: number | string | null) => versionId,
  (expiredVersions, versionId) => {
    if (isNil(versionId) || expiredVersions.length < 2) return false;

    // Archived if versionId is in the expired list starting from the second element
    return expiredVersions
      .slice(1)
      .some(version => version.versionId === versionId);
  },
);

/**
 * Checks whether to use the new Awards API if current route is an award
 * requirements page and if the award is any NOA or Palm
 */
export const useNewAwardAPISel = createSelector(
  routeParamsSel<{
    userId: string;
    advancementType: string;
    advancementId: string;
  }>,
  route => {
    const advId = route.advancementId;
    if (route.advancementType !== advancementTypes.AWARDS || !advId)
      return false;
    const awardId = Number(advId);
    const isPalm = PALM_AWARDS.has(awardId);
    const isNOA = NOA_AWARDS.has(awardId);
    return isPalm || isNOA;
  },
);

const hasYouthEditApprovePermissionSel = createSelector(
  [
    (state: RootState) =>
      youthEditApprovePermissions.map(p => hasNewPermissionSel(state, p)),
  ],
  (permissions): boolean => permissions.some(Boolean),
);

export const memberDetailsUserIdSel = createSelector(
  urlUserIdSel,
  selfUserIdSel,
  isYouthMemberSel,
  hasYouthEditApprovePermissionSel,
  isProfileRouteSel,
  (
    urlUserId,
    selfUserId,
    isYouthMember,
    hasYouthPermission,
    isProfileRoute,
  ) => {
    if (isYouthMember && hasYouthPermission) {
      return isProfileRoute ? selfUserId : urlUserId;
    }

    return isYouthMember ? selfUserId : urlUserId;
  },
);

export const youthActivitiesUserId = (state: RootState) =>
  routeParamsSel<RouteYouthActivityLogs>(state).userId;

export const createEagleActivityPayloadSel = createSelector(
  organizationGuidSel,
  youthProfileInfoSel,
  youthsAndAdultsSel,
  (organizationGuid, youthProfile, youthsAndAdults) => ({
    organizationGuid,
    userId: youthProfile?.userId as number,
    personGuid: youthProfile?.profile?.personGuid,
    youthProfile,
    youths: youthsAndAdults.youths,
  }),
);

export const areAllAdvancementRequirementsApprovedSel = createSelector(
  youthAdvRequirementsSel,
  advRequirements =>
    areAllAdvancementRequirementsApproved(advRequirements.requirements),
);
